import React, { useEffect, useState } from 'react';
import BEM from '../../bem';
import mobxInjectSelect from '../../utils/mobxInjectSelect';
import TCClient from '../../../client';
import { PBXDirectoryEntry } from '../../../types';
import Modal from '../Modal';

const classes = BEM.with('CreateOrEditPBXDirectoryEntryModal');

type CreateOrEditPBXDirectoryEntryModalProps = {
  options: {
    entry?: PBXDirectoryEntry;
    onSuccess?: () => void;
  };
};

type MobxProps = {
  closeModal: () => void;
  currentOrganizationId: string;
  isOpen: boolean;
};

const EMPTY_FORM = {
  extensionNumber: '',
  displayName: '',
  phoneNumber: '',
  location: '',
  department: '',
  title: '',
};

const CreateOrEditPBXDirectoryEntryModal = ({
  closeModal,
  currentOrganizationId,
  isOpen,
  options: { entry, onSuccess },
}: MobxProps & CreateOrEditPBXDirectoryEntryModalProps) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isEditing = Boolean(entry?.id);

  useEffect(() => {
    if (isOpen) {
      if (entry) {
        setFormData({
          extensionNumber: entry.extensionNumber || '',
          displayName: entry.displayName || '',
          phoneNumber: entry.phoneNumber || '',
          location: entry.location || '',
          department: entry.department || '',
          title: entry.title || '',
        });
      } else {
        setFormData(EMPTY_FORM);
      }
      setError('');
    }
  }, [isOpen, entry]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isValid = formData.extensionNumber.trim() !== '' && formData.displayName.trim() !== '';

  const handleSubmit = async () => {
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    setError('');

    try {
      if (isEditing && entry?.id) {
        await TCClient.pbx.updatePbxDirectoryEntry({
          orgToken: currentOrganizationId,
          id: entry.id,
          entry: {
            ...formData,
            organizationId: entry.organizationId,
          },
        });
      } else {
        await TCClient.pbx.createPbxDirectoryEntry({
          orgToken: currentOrganizationId,
          entry: formData,
        });
      }
      onSuccess?.();
      closeModal();
    } catch (err) {
      setError('Failed to save directory entry. Please try again.');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      bodyClass={classes('body')}
      closeClass={classes('close-btn')}
      footerClass={classes('footer')}
      header={isEditing ? 'Edit Directory Entry' : 'Create Directory Entry'}
      headerClass={classes('header')}
      isOpen={isOpen}
      size={'large'}
      className={classes()}
      onRequestClose={closeModal}
      footerPrimaryActions={
        <button
          aria-label="Save"
          className={classes('primary-btn')}
          type="button"
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      }
      footerSecondaryActions={
        <button
          aria-label="Cancel"
          className={classes('secondary-btn')}
          type="button"
          onClick={closeModal}
        >
          Cancel
        </button>
      }
    >
      <div className={classes('form-container')}>
        <div className={classes('form-field')}>
          <label className={classes('label')}>Extension Number *</label>
          <input
            className={classes('input')}
            type="text"
            value={formData.extensionNumber}
            onChange={(e) => handleChange('extensionNumber', e.target.value)}
            placeholder="Enter extension number"
          />
        </div>
        <div className={classes('form-field')}>
          <label className={classes('label')}>Display Name *</label>
          <input
            className={classes('input')}
            type="text"
            value={formData.displayName}
            onChange={(e) => handleChange('displayName', e.target.value)}
            placeholder="Enter display name"
          />
        </div>
        <div className={classes('form-field')}>
          <label className={classes('label')}>Phone Number</label>
          <input
            className={classes('input')}
            type="text"
            value={formData.phoneNumber}
            onChange={(e) => handleChange('phoneNumber', e.target.value)}
            placeholder="Enter phone number"
          />
        </div>
        <div className={classes('form-field')}>
          <label className={classes('label')}>Location</label>
          <input
            className={classes('input')}
            type="text"
            value={formData.location}
            onChange={(e) => handleChange('location', e.target.value)}
            placeholder="Enter location"
          />
        </div>
        <div className={classes('form-field')}>
          <label className={classes('label')}>Department</label>
          <input
            className={classes('input')}
            type="text"
            value={formData.department}
            onChange={(e) => handleChange('department', e.target.value)}
            placeholder="Enter department"
          />
        </div>
        <div className={classes('form-field')}>
          <label className={classes('label')}>Title</label>
          <input
            className={classes('input')}
            type="text"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="Enter title"
          />
        </div>
        {error && <div className={classes('error-message')}>{error}</div>}
      </div>
    </Modal>
  );
};

export default mobxInjectSelect<CreateOrEditPBXDirectoryEntryModalProps, MobxProps>({
  messengerStore: ['currentOrganizationId'],
})(CreateOrEditPBXDirectoryEntryModal);
